/**
 * The learner's own seat (Phase SC-4c, SC-21d).
 *
 * `GET /api/me/seat` is the only seating read a learner may make: it
 * resolves their `Student` row from their user id server-side and returns
 * *their* assignment and *their* bookings. The admin reads
 * (`/api/bookings`, `/api/zones`) stay admin-only — a learner asking "where
 * do I sit" must not be able to enumerate where everyone else sits.
 *
 * Check-in is the one write the tab offers: the learner arrives, taps, and
 * the centre's console sees them occupy the seat. The BE rejects a check-in
 * on a booking that isn't theirs, so no client-side guard is load-bearing.
 */

import * as React from "react";
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from "@tanstack/react-query";
import { ApiError } from "@eduviaos/shared";
import { MY_ATTENDANCE_KEY } from "../attendance/api";
import { slotNameLocalized } from "../i18n/portalShell";
import type { PortalLocale } from "../i18n/portalShell";
import { api } from "../api";

export type BookingStatus = "held" | "checked_in" | "completed" | "cancelled" | "no_show";

/**
 * `mode` on `MySeat` is what applies to this learner and is never
 * `mixed`; `centre_mode` can be `mixed`, in which case `plan` says how the
 * learner uses seats (Phase SC-22c).
 */
export type SeatMode = "none" | "zone_only" | "dynamic" | "fixed" | "mixed";

export type FixedAssignmentMethod = "admin_assigns" | "student_picks" | "auto_round_robin";

export type SeatPlanKind =
  | "fixed_seat"
  | "first_come"
  | "fixed_hours"
  | "flexible_hours"
  | "zone_only"
  | "no_seat";

export interface SeatPlan {
  kind: SeatPlanKind;
  source: "learner_plan" | "mixed_default";
  zone_id: string | null;
  zone_name: string | null;
  seat_id: string | null;
  seat_number: string | null;
  slot_keys: string[];
  /** `HH:MM:SS`, centre-local. */
  window_start: string | null;
  window_end: string | null;
  valid_from: string | null;
  valid_until: string | null;
}

export interface SeatPlace {
  seat_id: string;
  seat_number: string | null;
  zone_id: string | null;
  zone_name: string | null;
}

export interface SeatBooking {
  id: string;
  date_key: string;
  slot_key: string;
  status: BookingStatus;
  checked_in_at: string | null;
  place: SeatPlace | null;
  duration_minutes?: number | null;
  charge_paise?: number;
}

export interface MySeat {
  student_id: string;
  student_name: string;
  mode: SeatMode;
  fixed_assignment_method: FixedAssignmentMethod | null;
  require_booking: boolean;
  allow_seat_switch: boolean;
  allow_session_extension: boolean;
  enforce_zone_capacity: boolean;
  waitlist_enabled: boolean;
  custom_duration_enabled?: boolean;
  custom_hourly_rate_paise?: number;
  centre_mode?: SeatMode | null;
  plan?: SeatPlan | null;
  assigned: SeatPlace | null;
  today: SeatBooking | null;
  upcoming: SeatBooking[];
}

export type ZoneAssignmentMode = "inherit" | SeatMode;

export interface ZoneAvailabilityItem {
  zone_id: string;
  zone_name: string;
  slug: string;
  capacity: number;
  taken: number;
  available: number;
  is_full: boolean;
  waitlist_available: boolean;
  assignment_mode?: ZoneAssignmentMode;
  effective_mode?: SeatMode | null;
}

export interface SeatAvailabilityItem {
  seat_id: string;
  seat_number: string;
  zone_id: string;
  available: boolean;
  assigned_to_me: boolean;
}

export interface SeatAvailabilityResponse {
  mode: SeatMode;
  fixed_assignment_method: FixedAssignmentMethod | null;
  date_key: string;
  slot_key: string;
  enforce_zone_capacity: boolean;
  waitlist_enabled: boolean;
  zones: ZoneAvailabilityItem[];
  seats: SeatAvailabilityItem[] | null;
  slot: EffectiveSeatSlot;
}

export interface CreateBookingInput {
  seat_id?: string;
  zone_id?: string;
  date_key: string;
  slot_key: string;
  duration_minutes?: number;
  /** `HH:MM:SS`. Sent together with `duration_minutes` for an hourly booking. */
  custom_start?: string;
}

export interface BookingResponse {
  id: string;
  seat_id: string | null;
  zone_id: string | null;
  date_key: string;
  slot_key: string;
  status: string;
}

export interface ClaimSeatResponse {
  seat_id: string;
  assignment_method: string;
}

export type SeatSlotWeekday = "mon" | "tue" | "wed" | "thu" | "fri" | "sat" | "sun";

/** A shift the centre has configured, as it applies on one date (SC-21d d11–d19). */
export interface EffectiveSeatSlot {
  id: string;
  branch_id: string | null;
  key: string;
  label: string;
  /** `HH:MM:SS`, centre-local. */
  start_time: string;
  end_time: string;
  spans_midnight: boolean;
  days_of_week: SeatSlotWeekday[];
  effective_from: string | null;
  effective_until: string | null;
  display_order: number;
  is_active: boolean;
}

const MY_SEAT_KEY = ["my-seat"] as const;

function seatAvailabilityKey(dateKey: string, slotKey: string) {
  return ["seat-availability", dateKey, slotKey] as const;
}

/**
 * Both paths carry the `/api` prefix explicitly.
 *
 * `VITE_API_URL` points at the API origin with no path segment, and the
 * seating router bakes the prefix into each decorator
 * (`@router.get("/api/me/seat")`) rather than onto the router. Dropping
 * it 404s at runtime while every unit test still passes, because the
 * tests stub the client — which is exactly what happened on the first
 * stage run of this screen: the learner was told "not linked to your
 * centre's roster yet" over a seat that was assigned, booked and bound.
 */
export function useMySeat(enabled = true): UseQueryResult<MySeat> {
  return useQuery<MySeat>({
    queryKey: MY_SEAT_KEY,
    queryFn: () => api.get<MySeat>("/api/me/seat"),
    enabled,
    // A learner who isn't linked to a roster row gets a 403 that will not
    // resolve by asking again.
    retry: false,
  });
}

/**
 * The shifts bookable on `dateKey`, already resolved server-side: branch
 * slots replace tenant slots, and weekday / date-range / inactive filters
 * are applied. Ordered by `display_order`. An empty list is a real answer
 * (the centre is closed that day), not a loading gap.
 */
export function useEffectiveSeatSlots(
  dateKey: string,
  enabled = true,
): UseQueryResult<EffectiveSeatSlot[]> {
  return useQuery<EffectiveSeatSlot[]>({
    queryKey: ["seat-slots", dateKey] as const,
    queryFn: () =>
      api.get<EffectiveSeatSlot[]>(`/api/me/seat-slots?date=${encodeURIComponent(dateKey)}`),
    enabled: enabled && Boolean(dateKey),
    retry: false,
    staleTime: 5 * 60_000,
  });
}

/**
 * The centre's label wins — except where it is just the platform default
 * name for a default key ("Morning" on `morning`), which Hindi learners
 * should read in Hindi.
 */
export function slotDisplayName(
  slot: Pick<EffectiveSeatSlot, "key" | "label">,
  locale: PortalLocale = "en",
): string {
  const isDefaultName = slot.label.trim().toLowerCase() === slot.key.replace(/-/g, " ");
  if (isDefaultName) {
    const translated = slotNameLocalized(locale, slot.key);
    if (translated) return translated;
  }
  return slot.label;
}

/** `08:00:00`–`13:00:00` → `8:00 AM – 1:00 PM`. */
export function slotTimeRange(slot: Pick<EffectiveSeatSlot, "start_time" | "end_time">): string {
  const [sh = "00", sm = "00"] = slot.start_time.split(":");
  const [eh = "00", em = "00"] = slot.end_time.split(":");
  return `${clock(sh, sm)} – ${clock(eh, em)}`;
}

export function useSeatAvailability(
  dateKey: string,
  slotKey: string,
  enabled = true,
  pollMs: number | false = false,
): UseQueryResult<SeatAvailabilityResponse> {
  return useQuery<SeatAvailabilityResponse>({
    queryKey: seatAvailabilityKey(dateKey, slotKey),
    queryFn: () =>
      api.get<SeatAvailabilityResponse>(
        `/api/me/seat-availability?date=${encodeURIComponent(dateKey)}&slot=${encodeURIComponent(slotKey)}`,
      ),
    enabled: enabled && Boolean(dateKey && slotKey),
    retry: false,
    refetchInterval: pollMs,
  });
}

// ── Floor map + live seat colours (Phase SC-22c) ──────────────────────

export interface FloorRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface FloorZoneShape extends FloorRect {
  zone_id: string | null;
  label: string;
}

export interface FloorFeature extends FloorRect {
  kind: string;
  label: string | null;
  rotation: number;
}

export interface FloorSeatPlacement extends FloorRect {
  seat_id: string | null;
  zone_id: string | null;
  seat_number: string;
  rotation: number;
}

export interface FloorPlan {
  name: string;
  width: number;
  height: number;
  zones: FloorZoneShape[];
  features: FloorFeature[];
  seats: FloorSeatPlacement[];
}

/** The learner's branch floor plan, or `null` when the centre hasn't drawn one. */
export function useMyFloorPlan(enabled = true): UseQueryResult<FloorPlan | null> {
  return useQuery<FloorPlan | null>({
    queryKey: ["my-floor-plan"] as const,
    queryFn: async () => {
      try {
        return await api.get<FloorPlan>("/api/me/floor-plan");
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) return null;
        throw err;
      }
    },
    enabled,
    retry: false,
    staleTime: 5 * 60_000,
  });
}

export type SeatRealtimeState = "connecting" | "live" | "polling";

interface SeatStatusFrame {
  type: "seat.status";
  reason?: string;
  seat_id: string | null;
  status: "free" | "booked" | "occupied" | "fixed" | "blocked";
  date_key: string | null;
  slot_key: string | null;
}

function seatingWebsocketUrl(): string {
  const configured = import.meta.env.VITE_API_URL as string | undefined;
  const origin = configured?.startsWith("http")
    ? new URL(configured).origin
    : window.location.origin;
  const url = new URL("/api/seating/ws", origin);
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
  return url.toString();
}

/**
 * Live seat events for the learner's branch. A frame flips the seat's
 * colour in every cached availability for that date (and shift) straight
 * away, then refetches so capacity counts stay right. Frames never carry
 * who took a seat. While the socket is down the caller polls instead.
 */
export function useSeatRealtime(enabled = true): SeatRealtimeState {
  const qc = useQueryClient();
  const [state, setState] = React.useState<SeatRealtimeState>("connecting");

  React.useEffect(() => {
    if (!enabled || typeof WebSocket === "undefined") {
      setState("polling");
      return;
    }
    const token = api.getAccessToken();
    if (!token) {
      setState("polling");
      return;
    }
    let closed = false;
    let socket: WebSocket | null = null;
    let retry = 1000;
    let ping: number | undefined;
    const connect = () => {
      if (closed) return;
      socket = new WebSocket(seatingWebsocketUrl(), ["bearer", api.getAccessToken() ?? token]);
      socket.onopen = () => {
        retry = 1000;
        setState("live");
        void qc.invalidateQueries({ queryKey: ["seat-availability"] });
        ping = window.setInterval(() => socket?.send("ping"), 25_000);
      };
      socket.onmessage = (event) => {
        let frame: Partial<SeatStatusFrame>;
        try {
          frame = JSON.parse(String(event.data)) as Partial<SeatStatusFrame>;
        } catch {
          return;
        }
        if (frame.type !== "seat.status") return;
        if (frame.reason?.startsWith("floor_plan.")) {
          void qc.invalidateQueries({ queryKey: ["my-floor-plan"] });
          return;
        }
        if (!frame.seat_id || !frame.status) return;
        applySeatFrame(qc, frame as SeatStatusFrame);
        void qc.invalidateQueries({ queryKey: ["seat-availability"] });
      };
      socket.onclose = () => {
        window.clearInterval(ping);
        if (closed) return;
        setState("polling");
        window.setTimeout(connect, retry);
        retry = Math.min(retry * 2, 30_000);
      };
    };
    connect();
    return () => {
      closed = true;
      window.clearInterval(ping);
      socket?.close();
    };
  }, [enabled, qc]);

  return state;
}

function applySeatFrame(qc: ReturnType<typeof useQueryClient>, frame: SeatStatusFrame): void {
  const caches = qc.getQueriesData<SeatAvailabilityResponse>({ queryKey: ["seat-availability"] });
  for (const [key, current] of caches) {
    if (!current?.seats) continue;
    if (frame.date_key && frame.date_key !== current.date_key) continue;
    if (frame.slot_key && frame.slot_key !== current.slot_key) continue;
    qc.setQueryData<SeatAvailabilityResponse>(key, {
      ...current,
      seats: current.seats.map((s) =>
        s.seat_id === frame.seat_id && !s.assigned_to_me
          ? { ...s, available: frame.status === "free" }
          : s,
      ),
    });
  }
}

export interface WalkInCheckInResponse {
  booking: BookingResponse & { checked_in_at?: string | null };
  created: boolean;
}

/** `no_seat` plans: the learner checks in on arrival; once per day. */
export function useWalkInCheckIn() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<WalkInCheckInResponse>("/api/me/seat-check-in", {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: MY_SEAT_KEY });
      void qc.invalidateQueries({ queryKey: MY_ATTENDANCE_KEY });
    },
  });
}

export function useCreateBooking() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateBookingInput) => api.post<BookingResponse>("/api/bookings", body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: MY_SEAT_KEY });
      void qc.invalidateQueries({ queryKey: ["seat-availability"] });
    },
  });
}

export function useClaimSeat() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (seatId: string) =>
      api.post<ClaimSeatResponse>("/api/me/seat-claim", { seat_id: seatId }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: MY_SEAT_KEY });
      void qc.invalidateQueries({ queryKey: ["seat-availability"] });
    },
  });
}

/** Stored on the booking for the centre's audit trail, not shown to the learner. */
const LEARNER_CANCEL_REASON = "Cancelled by the learner in the portal.";

/** Release one of the learner's own bookings; the BE rejects anyone else's. */
export function useCancelBooking() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (bookingId: string) =>
      api.post<BookingResponse>(`/api/bookings/${bookingId}/cancel`, {
        reason: LEARNER_CANCEL_REASON,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: MY_SEAT_KEY });
      void qc.invalidateQueries({ queryKey: ["seat-availability"] });
    },
  });
}

export function useCheckIn() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (bookingId: string) => api.post(`/api/bookings/${bookingId}/check-in`, {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: MY_SEAT_KEY });
      void qc.invalidateQueries({ queryKey: MY_ATTENDANCE_KEY });
    },
  });
}

/** `YYYY-MM-DD` for a local calendar date (IST day keys from the learner's device). */
export function formatDateKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export function defaultBookingDateKey(): string {
  return formatDateKey(new Date());
}

/**
 * Slot keys are opaque by contract (`morning`, `slot-09-00-12-00`, or
 * whatever the centre typed). Make the common shapes readable and pass
 * anything else through untouched rather than mangling a label the centre
 * chose deliberately.
 *
 * The platform's own default keys get a translation; title-casing
 * `"morning"` left Hindi learners reading "Morning" on every shift chip,
 * the Today card and the booking confirmation.
 */
export function slotLabel(slotKey: string, locale: PortalLocale = "en"): string {
  const ranged = /^slot-(\d{2})-(\d{2})-(\d{2})-(\d{2})$/.exec(slotKey);
  if (ranged) {
    return `${clock(ranged[1]!, ranged[2]!)} – ${clock(ranged[3]!, ranged[4]!)}`;
  }
  const translated = slotNameLocalized(locale, slotKey);
  if (translated) return translated;
  if (/^[a-z]+$/.test(slotKey)) {
    return slotKey.charAt(0).toUpperCase() + slotKey.slice(1);
  }
  return slotKey;
}

/** Named shifts keep their label. Hourly bookings show the length and the charge. */
export function bookingWindowLabel(
  booking: Pick<SeatBooking, "slot_key" | "duration_minutes" | "charge_paise">,
  locale: PortalLocale = "en",
): string {
  if (
    booking.duration_minutes &&
    booking.duration_minutes > 0 &&
    booking.slot_key.startsWith("custom-")
  ) {
    const hours = Math.max(1, Math.round(booking.duration_minutes / 60));
    const hoursLabel = locale === "hi" ? `${hours} घंटे` : `${hours} hour${hours === 1 ? "" : "s"}`;
    if (booking.charge_paise && booking.charge_paise > 0) {
      const rupees = new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
        maximumFractionDigits: 0,
      }).format(booking.charge_paise / 100);
      return `${hoursLabel} · ${rupees}`;
    }
    return hoursLabel;
  }
  return slotLabel(booking.slot_key, locale);
}

/** `09:00:00` → `9:00 AM`. */
export function clockTime(value: string): string {
  const [hh = "00", mm = "00"] = value.split(":");
  return clock(hh, mm);
}

function clock(hh: string, mm: string): string {
  const h = Number(hh);
  const suffix = h < 12 ? "AM" : "PM";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${mm} ${suffix}`;
}

/**
 * `2026-08-04` → `Tue, Aug 4` (or the Hindi equivalent).
 * Dates come as IST day keys, not instants.
 */
export function dayLabel(dateKey: string, locale = "en-IN"): string {
  const [y, m, d] = dateKey.split("-").map(Number);
  if (!y || !m || !d) return dateKey;
  return new Date(y, m - 1, d).toLocaleDateString(locale, {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

export function isFixedReadOnlySeat(seat: MySeat): boolean {
  if (seat.mode !== "fixed") return false;
  if (seat.fixed_assignment_method === "admin_assigns") return true;
  if (seat.fixed_assignment_method === "auto_round_robin") return true;
  if (seat.fixed_assignment_method === "student_picks" && seat.assigned) return true;
  return false;
}

export function needsSeatClaim(seat: MySeat): boolean {
  return (
    seat.mode === "fixed" && seat.fixed_assignment_method === "student_picks" && !seat.assigned
  );
}

/**
 * A no-seat plan resolves to `mode: "none"`, but the learner still checks
 * in from the tab. Only a centre with no seating and no plan hides it.
 */
export function showsSeatTab(seat: MySeat): boolean {
  return seat.mode !== "none" || Boolean(seat.plan);
}

export function canBookSessions(seat: MySeat): boolean {
  return seat.mode === "zone_only" || seat.mode === "dynamic";
}
